import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { OnboardingArrow } from './OnboardingArrow';
import { OnboardingCard } from './OnboardingCard';
import { OnboardingSkipConfirm } from './OnboardingSkipConfirm';
import { DEFAULT_SPOTLIGHT_PADDING, DEFAULT_SPOTLIGHT_RADIUS, ONBOARDING_Z_INDEX } from './constants';
import {
  findOnboardingPanels,
  findOnboardingTarget,
  findOnboardingTargets,
  measureSpotlight,
  spotlightListsEqual,
  spotlightRectsEqual,
  unionRects,
  type SpotlightRect,
} from './findTarget';
import { sizesEqual, type Size } from './geometry';
import { computePlacement, isCompactViewport, sideForPlacement } from './placement';
import { scrollOnboardingTargetIntoView } from './scrollIntoView';
import type { OnboardingStep } from './types';

interface OnboardingOverlayProps {
  step: OnboardingStep;
  onPrimary: () => void;
  onSecondary: () => void;
  onBack?: () => void;
  actionCompleted?: boolean;
  onTaskStart?: (branch: string) => void;
  onComplete?: () => void;
}

const NO_HOLES: SpotlightRect[] = [];

/** Styled in `index.css`; set here so the rule lives with the rest of the theme. */
const HIGHLIGHT_ATTRIBUTE = 'data-onboarding-highlight';

/**
 * `visualViewport` is what the user can actually see once the mobile keyboard or
 * the collapsing browser chrome is in play; `innerWidth/Height` is not.
 */
function readViewport(): Size {
  const visual = window.visualViewport;
  return {
    width: Math.round(visual?.width ?? window.innerWidth),
    height: Math.round(visual?.height ?? window.innerHeight),
  };
}

export function OnboardingOverlay({ step, onPrimary, onSecondary, onBack, onTaskStart, onComplete, actionCompleted }: OnboardingOverlayProps) {
  const maskId = useId().replace(/:/g, '');
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrolledToTarget = useRef(false);
  const highlightedElements = useRef(new Set<HTMLElement>());
  const [targetWaitExpired, setTargetWaitExpired] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Size>(readViewport);
  const [holes, setHoles] = useState<SpotlightRect[]>(NO_HOLES);
  const [actionTargetReady, setActionTargetReady] = useState(false);
  const [anchor, setAnchor] = useState<SpotlightRect | null>(null);
  const [emphasisRect, setEmphasisRect] = useState<SpotlightRect | null>(null);
  const [cardSize, setCardSize] = useState<Size>({ width: 0, height: 0 });
  const [confirmingSkip, setConfirmingSkip] = useState(false);

  const isRestart = step.secondaryAction === 'restart';
  const padding = step.padding ?? DEFAULT_SPOTLIGHT_PADDING;
  const radius = step.radius ?? DEFAULT_SPOTLIGHT_RADIUS;

  useEffect(() => {
    setConfirmingSkip(false);
  }, [step.id]);

  /**
   * One measurement pass. State is only replaced when a box actually moved, so the
   * measurement loop costs a few `getBoundingClientRect` calls and no renders
   * while the page is still.
   */
  const measure = useCallback(() => {
    const nextViewport = readViewport();
    setViewport(prev => (sizesEqual(prev, nextViewport) ? prev : nextViewport));

    if (!step.target) {
      setHoles(prev => (prev.length === 0 ? prev : NO_HOLES));
      setAnchor(null);
      setEmphasisRect(null);
      return;
    }

    const actionEl = findOnboardingTarget(step.arrowTarget ?? step.target);
    setActionTargetReady(Boolean(actionEl && !actionEl.matches(':disabled, [aria-disabled="true"]')));
    const targets = findOnboardingTargets(step.target);
    if (targets.length === 0) {
      setHoles(prev => (prev.length === 0 ? prev : NO_HOLES));
      setAnchor(null);
      setEmphasisRect(null);
      return;
    }
    if (!scrolledToTarget.current) {
      scrolledToTarget.current = true;
      scrollOnboardingTargetIntoView(targets[0], isCompactViewport(nextViewport), 'auto');
    }
    if (step.highlightTarget) {
      findOnboardingTargets(step.arrowTarget ?? step.target).forEach(el => {
        if (!highlightedElements.current.has(el)) {
          el.setAttribute(HIGHLIGHT_ATTRIBUTE, '');
          highlightedElements.current.add(el);
        }
      });
    }
    const els = [...targets, ...findOnboardingPanels(step.target)];

    const raw =
      step.separateHoles && els.length > 1
        ? els
            .slice()
            .sort((a, b) => {
              const ra = a.getBoundingClientRect();
              const rb = b.getBoundingClientRect();
              return ra.top - rb.top || ra.left - rb.left;
            })
            .map(el => measureSpotlight(el, padding, radius))
        : [
            els.length === 1
              ? measureSpotlight(els[0], padding, radius)
              : unionRects(els.map(el => measureSpotlight(el, padding, radius))),
          ];
    const measured = raw.map(h => {
      const x = Math.max(0, h.x);
      const y = Math.max(0, h.y);
      return {
        ...h,
        x,
        y,
        width: Math.max(0, Math.min(nextViewport.width, h.x + h.width) - x),
        height: Math.max(0, Math.min(nextViewport.height, h.y + h.height) - y),
      };
    });
    const bounds = measured.length === 1 ? measured[0] : unionRects(measured);
    setHoles(prev => (spotlightListsEqual(prev, measured) ? prev : measured));

    const arrowEl = step.arrowTarget ? findOnboardingTarget(step.arrowTarget) : null;
    const nextAnchor = arrowEl
      ? measureSpotlight(arrowEl, padding, radius)
      : step.separateHoles
        ? measured[0]
        : bounds;
    setAnchor(prev => (spotlightRectsEqual(prev, nextAnchor) ? prev : nextAnchor));

    if (step.emphasisTarget) {
      const emphasisEl = findOnboardingTarget(step.emphasisTarget);
      const nextEmphasis = emphasisEl ? measureSpotlight(emphasisEl, 2, 8) : null;
      setEmphasisRect(prev => (spotlightRectsEqual(prev, nextEmphasis) ? prev : nextEmphasis));
    } else {
      setEmphasisRect(null);
    }
  }, [step.target, step.arrowTarget, step.separateHoles, step.emphasisTarget, step.highlightTarget, padding, radius]);

  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    const elements = highlightedElements.current;
    return () => elements.forEach(el => el.removeAttribute(HIGHLIGHT_ATTRIBUTE));
  }, []);

  // Follow scrolling immediately and late mounts at a bounded rate. Avoid traversing
  // target ancestors sixty times a second while the page is idle.
  useEffect(() => {
    const timer = window.setInterval(measure, 200);
    window.addEventListener('resize', measure);
    document.addEventListener('scroll', measure, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', measure);
      document.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  useEffect(() => {
    const timer = window.setTimeout(() => setTargetWaitExpired(true), 1500);
    return () => window.clearTimeout(timer);
  }, []);

  // The card grows when its illustration finishes loading or the text wraps
  // differently — re-place it instead of leaving the arrow pointing at thin air.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const read = () => {
      const rect = el.getBoundingClientRect();
      const next = { width: rect.width, height: rect.height };
      setCardSize(prev => (sizesEqual(prev, next) ? prev : next));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [step.id]);

  useEffect(() => {
    const scope = () => {
      const dialog = overlayRef.current?.querySelector<HTMLElement>(confirmingSkip ? '[role="alertdialog"]' : '[role="dialog"]');
      const targets = !confirmingSkip && step.allowTargetInteraction && step.target
        ? [...findOnboardingTargets(step.target), ...findOnboardingPanels(step.target)] : [];
      return dialog ? [dialog, ...targets] : targets;
    };
    const focusable = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const controls = () => Array.from(new Set(scope().flatMap(root => [
      ...(root.matches(focusable) ? [root] : []),
      ...Array.from(root.querySelectorAll<HTMLElement>(focusable)),
    ]))).filter(el => el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden');
    const focusDialog = () => {
      const dialog = scope()[0];
      (confirmingSkip ? controls()[0] ?? dialog : dialog)?.focus({ preventScroll: true });
    };
    const frame = requestAnimationFrame(focusDialog);
    const onFocus = (event: FocusEvent) => {
      if (!scope().some(root => root.contains(event.target as Node))) focusDialog();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (confirmingSkip) setConfirmingSkip(false);
        else if (step.layout === 'complete') (onComplete ?? onPrimary)();
        else setConfirmingSkip(true);
      } else if (event.key === 'Tab') {
        const items = controls();
        if (!items.length) { event.preventDefault(); return; }
        const index = items.indexOf(document.activeElement as HTMLElement);
        const nextIndex = event.shiftKey ? (index <= 0 ? items.length - 1 : index - 1) : (index + 1) % items.length;
        event.preventDefault();
        items[nextIndex].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocus);
    };
  }, [step, onPrimary, onComplete, confirmingSkip]);

  useEffect(() => {
    const clickId = step.arrowTarget ?? step.target;
    if (!step.advanceOnTargetClick || !clickId) return;
    const el = findOnboardingTarget(clickId);
    if (!el) return;
    const onClick = () => onPrimary();
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [step.id, step.target, step.arrowTarget, step.advanceOnTargetClick, onPrimary, anchor]);

  /**
   * A native <select> cannot advance on a click — the click lands before the user has
   * chosen anything. Those steps declare a predicate instead and advance once it holds.
   * The ref keeps one advance per step: `next()` is not idempotent.
   */
  const advancedRef = useRef(false);
  useEffect(() => {
    advancedRef.current = false;
  }, [step.id]);
  useEffect(() => {
    const advanceWhen = step.advanceWhen;
    if (!advanceWhen || confirmingSkip || actionCompleted) return;
    const timer = window.setInterval(() => {
      if (advancedRef.current || !advanceWhen()) return;
      advancedRef.current = true;
      onPrimary();
    }, 200);
    return () => window.clearInterval(timer);
  }, [step.id, step.advanceWhen, confirmingSkip, onPrimary, actionCompleted]);

  const spotlight = useMemo(
    () => (holes.length === 0 ? null : holes.length === 1 ? holes[0] : unionRects(holes)),
    [holes]
  );

  const placement = useMemo(
    () =>
      computePlacement({
        spotlight,
        anchor,
        card: cardSize,
        viewport,
        preference: sideForPlacement(step.placement),
        showArrow: Boolean(step.showArrow),
      }),
    [spotlight, anchor, cardSize, viewport, step.placement, step.showArrow]
  );

  const measured = cardSize.width > 0 && cardSize.height > 0;
  // Missing/filtered/late targets must always leave a visible way to continue or skip.
  const hasUsableSpotlight =
    spotlight != null && spotlight.width >= 8 && spotlight.height >= 8;
  const cardReady = measured && (!step.waitForTarget || hasUsableSpotlight || targetWaitExpired);
  const visibleHoles = confirmingSkip ? NO_HOLES : holes;
  const hole = visibleHoles[0] ?? null;
  const catcher = confirmingSkip || !step.allowTargetInteraction;

  const handleSecondary = () => {
    if (isRestart || step.secondaryAction === 'complete') {
      onSecondary();
      return;
    }
    setConfirmingSkip(true);
  };

  return createPortal(
    <div ref={overlayRef} className="pointer-events-none fixed inset-0" style={{ zIndex: ONBOARDING_Z_INDEX }} data-onboarding-overlay>
      <svg className="pointer-events-none absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            {visibleHoles.map((h, i) => (
              <rect
                key={`mask-${i}`}
                x={h.x}
                y={h.y}
                width={h.width}
                height={h.height}
                rx={h.radius}
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="#00000099"
          mask={`url(#${maskId})`}
        />
        {visibleHoles.map((h, i) => (
          <rect
            key={`stroke-${i}`}
            x={h.x}
            y={h.y}
            width={h.width}
            height={h.height}
            rx={h.radius}
            fill="none"
            stroke="white"
            strokeOpacity="0.35"
            strokeWidth="2"
          />
        ))}
        {!confirmingSkip && emphasisRect && (
          <rect
            x={emphasisRect.x}
            y={emphasisRect.y}
            width={emphasisRect.width}
            height={emphasisRect.height}
            rx={8}
            fill="none"
            stroke="#0E8965"
            strokeWidth="2"
          />
        )}
      </svg>

      {catcher ? (
        <div className="pointer-events-auto absolute inset-0" />
      ) : hole ? (
        <>
          <div className="pointer-events-auto absolute left-0 right-0 top-0" style={{ height: Math.max(0, hole.y) }} />
          <div
            className="pointer-events-auto absolute left-0"
            style={{ top: hole.y, width: Math.max(0, hole.x), height: hole.height }}
          />
          <div
            className="pointer-events-auto absolute right-0"
            style={{
              top: hole.y,
              width: Math.max(0, viewport.width - hole.x - hole.width),
              height: hole.height,
            }}
          />
          <div
            className="pointer-events-auto absolute bottom-0 left-0 right-0"
            style={{ height: Math.max(0, viewport.height - hole.y - hole.height) }}
          />
        </>
      ) : (
        <div className="pointer-events-auto absolute inset-0" />
      )}

      {!confirmingSkip && cardReady && placement.arrow && <OnboardingArrow arrow={placement.arrow} />}

      <div
        ref={cardRef}
        className="pointer-events-auto absolute"
        aria-hidden={confirmingSkip || !cardReady}
        style={{
          left: placement.card.x,
          top: placement.card.y,
          zIndex: 2,
          visibility: confirmingSkip || !cardReady ? 'hidden' : 'visible',
        }}
      >
        <OnboardingCard
          step={step}
          onPrimary={onPrimary}
          onSecondary={handleSecondary}
          onBack={onBack}
          actionCompleted={actionCompleted}
          onTaskStart={onTaskStart}
          maxHeight={placement.cardMaxHeight}
          targetUnavailable={Boolean(step.target && targetWaitExpired && (!hasUsableSpotlight || ((step.advanceOnTargetClick || step.advanceWhen) && !actionTargetReady)))}
        />
      </div>

      {confirmingSkip && (
        <div className="pointer-events-auto absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2">
          <OnboardingSkipConfirm
            onCancel={() => setConfirmingSkip(false)}
            onConfirm={onSecondary}
          />
        </div>
      )}
    </div>,
    document.body
  );
}
